import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, CheckCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { trackPaymentSuccess } from '../utils/analytics';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(
        `${API}/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
        
        // Track successful payment
        trackPaymentSuccess(res.data.payment_type, res.data.amount);
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus();
        }, pollInterval);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Wrench className="w-12 h-12 text-[#003B5C]" />
          </div>

          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-[#003B5C] animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#003B5C] mb-2">Processing Payment</h2>
              <p className="text-[#475569]">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-[#009B4D] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#009B4D] mb-2">Payment Successful!</h2>
              <p className="text-[#475569] mb-6">Thank you for your payment. Your transaction has been completed successfully.</p>
              <Button
                data-testid="goto-dashboard-btn"
                onClick={() => navigate('/')}
                className="w-full bg-[#003B5C] hover:bg-[#002A42] text-white rounded-full py-3 font-bold"
              >
                Go to Home
              </Button>
            </>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-3xl">✕</span>
              </div>
              <h2 className="text-2xl font-bold text-red-600 mb-2">
                {status === 'timeout' ? 'Payment Check Timeout' : status === 'expired' ? 'Payment Expired' : 'Payment Error'}
              </h2>
              <p className="text-[#475569] mb-6">
                {status === 'timeout'
                  ? 'Unable to verify payment status. Please check your email for confirmation.'
                  : status === 'expired'
                  ? 'Your payment session has expired. Please try again.'
                  : 'There was an error processing your payment. Please try again.'}
              </p>
              <Button
                data-testid="back-home-btn"
                onClick={() => navigate('/')}
                className="w-full bg-[#003B5C] hover:bg-[#002A42] text-white rounded-full py-3 font-bold"
              >
                Back to Home
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;